import React, { Suspense, lazy, useMemo, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { createAppTheme } from './styles/theme';
import { createAntdTheme } from './styles/antdTheme';
import { AppThemeMode, ThemeModeContext } from './contexts/ThemeModeContext';
import ProtectedRoute from './routes/ProtectedRoute';
import PermissionRoute from './routes/PermissionRoute';
import MainLayout from './components/layout/MainLayout';
import authApi from './api/authApi';
import { useAuthStore } from './store/authStore';
import { setTelemetryUser, trackPageView } from './telemetry/appInsights';

const lazyDefault = (loader: () => Promise<{ default: React.ComponentType<any> }>) => lazy(loader);
const lazyNamed = (loader: () => Promise<any>, name: string) => lazy(() => loader().then(module => ({ default: module[name] })));
const LoginPage = lazyDefault(() => import('./pages/Auth/LoginPage'));
const ForgotPasswordPage = lazyDefault(() => import('./pages/Auth/ForgotPasswordPage'));
const ResetPasswordPage = lazyDefault(() => import('./pages/Auth/ResetPasswordPage'));
const DashboardPage = lazyDefault(() => import('./pages/Dashboard/DashboardPage'));
const PoliciesPage = lazyNamed(() => import('./pages/Knowledge/DocumentsPage'), 'PoliciesPage');
const ProceduresPage = lazyNamed(() => import('./pages/Knowledge/DocumentsPage'), 'ProceduresPage');
const StandingOrdersPage = lazyNamed(() => import('./pages/Knowledge/DocumentsPage'), 'StandingOrdersPage');
const ResourcesPage = lazyDefault(() => import('./pages/Knowledge/ResourcesPage'));
const PolicyFormPage = lazyNamed(() => import('./pages/Knowledge/DocumentFormPage'), 'PolicyFormPage');
const ProcedureFormPage = lazyNamed(() => import('./pages/Knowledge/DocumentFormPage'), 'ProcedureFormPage');
const StandingOrderFormPage = lazyNamed(() => import('./pages/Knowledge/DocumentFormPage'), 'StandingOrderFormPage');
const ResourceFormPage = lazyNamed(() => import('./pages/Knowledge/DocumentFormPage'), 'ResourceFormPage');
const PolicyViewPage = lazyNamed(() => import('./pages/Knowledge/DocumentViewPage'), 'PolicyViewPage');
const ProcedureViewPage = lazyNamed(() => import('./pages/Knowledge/DocumentViewPage'), 'ProcedureViewPage');
const StandingOrderViewPage = lazyNamed(() => import('./pages/Knowledge/DocumentViewPage'), 'StandingOrderViewPage');
const ResourceViewPage = lazyNamed(() => import('./pages/Knowledge/DocumentViewPage'), 'ResourceViewPage');
const QuizzesPage = lazyDefault(() => import('./pages/Knowledge/QuizzesPage'));
const QuizTakerPage = lazyDefault(() => import('./pages/Knowledge/QuizTakerPage'));
const AIPolicyCreatorPage = lazyDefault(() => import('./pages/Knowledge/AIPolicyCreatorPage'));
const StandardsWatchPage = lazyDefault(() => import('./pages/Knowledge/StandardsWatchPage'));
const AIDocumentSearchPage = lazyDefault(() => import('./pages/Knowledge/AIDocumentSearchPage'));
const AIKnowledgeGraphPage = lazyDefault(() => import('./pages/Knowledge/AIKnowledgeGraphPage'));
const IncidentsPage = lazyDefault(() => import('./pages/Compliance/IncidentsPage'));
const NearMissesPage = lazyDefault(() => import('./pages/Compliance/NearMissesPage'));
const RisksPage = lazyDefault(() => import('./pages/Compliance/RisksPage'));
const RiskRegisterPage = lazyDefault(() => import('./pages/Compliance/RiskRegisterPage'));
const RTAPsPage = lazyDefault(() => import('./pages/Compliance/RTAPsPage'));
const AuditsPage = lazyDefault(() => import('./pages/Compliance/AuditsPage'));
const NCRsPage = lazyDefault(() => import('./pages/Compliance/NCRsPage'));
const MasterRegisterPage = lazyDefault(() => import('./pages/Compliance/MasterRegisterPage'));
const IncidentFormPage = lazyDefault(() => import('./pages/Compliance/IncidentFormPage'));
const IncidentViewPage = lazyDefault(() => import('./pages/Compliance/IncidentViewPage'));
const NearMissFormPage = lazyDefault(() => import('./pages/Compliance/NearMissFormPage'));
const AuditFormPage = lazyDefault(() => import('./pages/Compliance/AuditFormPage'));
const RiskAssessmentFormPage = lazyDefault(() => import('./pages/Compliance/RiskAssessmentFormPage'));
const NCRFormPage = lazyDefault(() => import('./pages/Compliance/NCRFormPage'));
const RTAPFormPage = lazyDefault(() => import('./pages/Compliance/RTAPFormPage'));
const AuditViewPage = lazyNamed(() => import('./pages/Compliance/ComplianceDetailViewPages'), 'AuditViewPage');
const NCRViewPage = lazyNamed(() => import('./pages/Compliance/ComplianceDetailViewPages'), 'NCRViewPage');
const NearMissViewPage = lazyNamed(() => import('./pages/Compliance/ComplianceDetailViewPages'), 'NearMissViewPage');
const RiskAssessmentViewPage = lazyNamed(() => import('./pages/Compliance/ComplianceDetailViewPages'), 'RiskAssessmentViewPage');
const RiskRegisterViewPage = lazyNamed(() => import('./pages/Compliance/ComplianceDetailViewPages'), 'RiskRegisterViewPage');
const RTAPViewPage = lazyNamed(() => import('./pages/Compliance/ComplianceDetailViewPages'), 'RTAPViewPage');
const PrivacyIncidentsPage = lazyNamed(() => import('./pages/Privacy/PrivacyPages'), 'PrivacyIncidentsPage');
const PIAsPage = lazyNamed(() => import('./pages/Privacy/PrivacyPages'), 'PIAsPage');
const SARsPage = lazyNamed(() => import('./pages/Privacy/PrivacyPages'), 'SARsPage');
const PrivacyIncidentFormPage = lazyDefault(() => import('./pages/Privacy/PrivacyIncidentFormPage'));
const PIAFormPage = lazyDefault(() => import('./pages/Privacy/PIAFormPage'));
const SARFormPage = lazyDefault(() => import('./pages/Privacy/SARFormPage'));
const PIAViewPage = lazyNamed(() => import('./pages/Privacy/PrivacyDetailViewPages'), 'PIAViewPage');
const PrivacyIncidentViewPage = lazyNamed(() => import('./pages/Privacy/PrivacyDetailViewPages'), 'PrivacyIncidentViewPage');
const SARViewPage = lazyNamed(() => import('./pages/Privacy/PrivacyDetailViewPages'), 'SARViewPage');
const ComplaintsPage = lazyDefault(() => import('./pages/Management/ComplaintsPage'));
const ComplaintFormPage = lazyDefault(() => import('./pages/Management/ComplaintFormPage'));
const ComplaintViewPage = lazyDefault(() => import('./pages/Management/ComplaintViewPage'));
const AdminPage = lazyDefault(() => import('./pages/Admin/AdminPage'));
const UsersPage = lazyDefault(() => import('./pages/Admin/UsersPage'));
const UserFormPage = lazyDefault(() => import('./pages/Admin/UserFormPage'));
const RolesPage = lazyDefault(() => import('./pages/Admin/RolesPage'));
const RoleFormPage = lazyDefault(() => import('./pages/Admin/RoleFormPage'));
const OrganisationsPage = lazyDefault(() => import('./pages/Admin/OrganisationsPage'));
const OrganisationFormPage = lazyDefault(() => import('./pages/Admin/OrganisationFormPage'));
const OrganisationViewPage = lazyNamed(() => import('./pages/Admin/AdminDetailViewPages'), 'OrganisationViewPage');
const RoleViewPage = lazyNamed(() => import('./pages/Admin/AdminDetailViewPages'), 'RoleViewPage');
const UserViewPage = lazyNamed(() => import('./pages/Admin/AdminDetailViewPages'), 'UserViewPage');
const AuditLogsPage = lazyDefault(() => import('./pages/Admin/AuditLogsPage'));
const LookupsPage = lazyDefault(() => import('./pages/Admin/LookupsPage'));
const NotificationTemplatesPage = lazyDefault(() => import('./pages/Admin/NotificationTemplatesPage'));
const DistributionGroupsPage = lazyDefault(() => import('./pages/Management/DistributionGroupsPage'));
const ProfilePage = lazyDefault(() => import('./pages/Auth/ProfilePage'));
const SettingsPage = lazyDefault(() => import('./pages/Auth/SettingsPage'));

const TelemetryRouteTracker: React.FC = () => {
  const location = useLocation();

  React.useEffect(() => {
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return null;
};

function App() {
  const { isAuthenticated, user, accessToken, setAuth, setUser, logout } = useAuthStore();
  const [mode, setMode] = useState<AppThemeMode>(() => {
    const saved = window.localStorage.getItem('mmc-theme-mode');
    return saved === 'dark' ? 'dark' : 'light';
  });

  const theme = useMemo(() => createAppTheme(mode), [mode]);
  const antTheme = useMemo(() => createAntdTheme(mode), [mode]);

  React.useEffect(() => {
    window.document.documentElement.dataset.mmcTheme = mode;
  }, [mode]);

  React.useEffect(() => {
    setTelemetryUser(isAuthenticated ? user?.userId : null);
  }, [isAuthenticated, user?.userId]);

  React.useEffect(() => {
    if (!isAuthenticated || accessToken) return;

    let cancelled = false;
    const refreshSession = async () => {
      try {
        const response = await authApi.refreshToken();
        const auth = response.data?.data || response.data;
        if (!cancelled && auth?.user && auth?.accessToken) {
          setAuth(auth.user, auth.accessToken, auth.refreshToken || '');
        }
      } catch {
        if (!cancelled) logout();
      }
    };

    refreshSession();
    return () => {
      cancelled = true;
    };
  }, [accessToken, isAuthenticated, logout, setAuth]);

  React.useEffect(() => {
    if (!isAuthenticated || !accessToken) return;

    let cancelled = false;
    const refreshUserContext = async () => {
      try {
        const response = await authApi.getUserContext();
        const user = response.data?.data || response.data;
        if (!cancelled && user) setUser(user);
      } catch {}
    };

    refreshUserContext();
    const handleFocus = () => refreshUserContext();
    window.addEventListener('focus', handleFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', handleFocus);
    };
  }, [accessToken, isAuthenticated, setUser]);

  const themeModeValue = useMemo(() => ({
    mode,
    toggleMode: () => {
      setMode(current => {
        const next = current === 'dark' ? 'light' : 'dark';
        window.localStorage.setItem('mmc-theme-mode', next);
        return next;
      });
    },
  }), [mode]);

  return (
    <ConfigProvider theme={antTheme}>
      <ThemeModeContext.Provider value={themeModeValue}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <TelemetryRouteTracker />
          <Suspense fallback={<div role="status" aria-label="Loading page" style={{ minHeight: '40vh' }} />}>
          <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route element={<ProtectedRoute />}>
            <Route element={<MainLayout />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route element={<PermissionRoute permission="knowledge.documents.read" />}>
                <Route path="/knowledge/policies" element={<PoliciesPage />} />
                <Route path="/knowledge/policies/:id" element={<PolicyViewPage />} />
                <Route path="/knowledge/procedures" element={<ProceduresPage />} />
                <Route path="/knowledge/procedures/:id" element={<ProcedureViewPage />} />
                <Route path="/knowledge/standing-orders" element={<StandingOrdersPage />} />
                <Route path="/knowledge/standing-orders/:id" element={<StandingOrderViewPage />} />
                <Route path="/knowledge/resources" element={<ResourcesPage />} />
                <Route path="/knowledge/resources/:id" element={<ResourceViewPage />} />
                <Route path="/knowledge/quizzes" element={<QuizzesPage />} />
                <Route path="/knowledge/quizzes/:id/take" element={<QuizTakerPage />} />
              </Route>
              <Route element={<PermissionRoute permission="knowledge.documents.create" />}>
                <Route path="/knowledge/policies/new" element={<PolicyFormPage />} />
                <Route path="/knowledge/procedures/new" element={<ProcedureFormPage />} />
                <Route path="/knowledge/standing-orders/new" element={<StandingOrderFormPage />} />
                <Route path="/knowledge/resources/new" element={<ResourceFormPage />} />
              </Route>
              <Route element={<PermissionRoute permission="knowledge.standards.read" />}>
                <Route path="/knowledge/standards-watch" element={<StandardsWatchPage />} />
              </Route>
              <Route element={<PermissionRoute permission="knowledge.documents.update" />}>
                <Route path="/knowledge/policies/:id/edit" element={<PolicyFormPage />} />
                <Route path="/knowledge/procedures/:id/edit" element={<ProcedureFormPage />} />
                <Route path="/knowledge/standing-orders/:id/edit" element={<StandingOrderFormPage />} />
                <Route path="/knowledge/resources/:id/edit" element={<ResourceFormPage />} />
              </Route>
              <Route element={<PermissionRoute permission="ai.use" />}>
                <Route path="/knowledge/ai-creator" element={<AIPolicyCreatorPage />} />
                <Route path="/knowledge/ai-search" element={<AIDocumentSearchPage />} />
                <Route path="/knowledge/ai-graph" element={<AIKnowledgeGraphPage />} />
              </Route>
              <Route element={<PermissionRoute permission="compliance.audits.read" />}>
                <Route path="/compliance/audits" element={<AuditsPage />} />
                <Route path="/compliance/audits/:id/view" element={<AuditViewPage />} />
              </Route>
              <Route element={<PermissionRoute permission="compliance.audits.create" />}>
                <Route path="/compliance/audits/new" element={<AuditFormPage />} />
              </Route>
              <Route element={<PermissionRoute permission="compliance.audits.update" />}>
                <Route path="/compliance/audits/:id/run" element={<AuditFormPage />} />
                <Route path="/compliance/audits/:id" element={<AuditFormPage />} />
              </Route>
              <Route element={<PermissionRoute permission="compliance.risks.read" />}>
                <Route path="/compliance/risk-assessments" element={<RisksPage />} />
                <Route path="/compliance/risk-assessments/:id/view" element={<RiskAssessmentViewPage />} />
                <Route path="/compliance/risk-register" element={<RiskRegisterPage />} />
                <Route path="/compliance/risk-register/:id/view" element={<RiskRegisterViewPage />} />
                <Route path="/compliance/rtaps" element={<RTAPsPage />} />
                <Route path="/compliance/rtaps/:id/view" element={<RTAPViewPage />} />
                <Route path="/compliance/ncrs" element={<NCRsPage />} />
                <Route path="/compliance/ncrs/:id/view" element={<NCRViewPage />} />
              </Route>
              <Route element={<PermissionRoute permission="compliance.risks.create" />}>
                <Route path="/compliance/risk-assessments/new" element={<RiskAssessmentFormPage />} />
                <Route path="/compliance/rtaps/new" element={<RTAPFormPage />} />
                <Route path="/compliance/ncrs/new" element={<NCRFormPage />} />
              </Route>
              <Route element={<PermissionRoute permission="compliance.risks.update" />}>
                <Route path="/compliance/risk-assessments/:id" element={<RiskAssessmentFormPage />} />
                <Route path="/compliance/rtaps/:id" element={<RTAPFormPage />} />
                <Route path="/compliance/ncrs/:id" element={<NCRFormPage />} />
              </Route>
              <Route element={<PermissionRoute permission="compliance.incidents.read" />}>
                <Route path="/compliance/incidents" element={<IncidentsPage />} />
                <Route path="/compliance/incidents/:id/view" element={<IncidentViewPage />} />
                <Route path="/compliance/near-misses" element={<NearMissesPage />} />
                <Route path="/compliance/near-misses/:id/view" element={<NearMissViewPage />} />
              </Route>
              <Route element={<PermissionRoute permission="compliance.incidents.create" />}>
                <Route path="/compliance/incidents/new" element={<IncidentFormPage />} />
                <Route path="/compliance/near-misses/new" element={<NearMissFormPage />} />
              </Route>
              <Route element={<PermissionRoute permission="compliance.incidents.update" />}>
                <Route path="/compliance/incidents/:id" element={<IncidentFormPage />} />
                <Route path="/compliance/near-misses/:id" element={<NearMissFormPage />} />
              </Route>
              <Route element={<PermissionRoute anyOf={['compliance.incidents.read', 'compliance.audits.read', 'compliance.risks.read']} />}>
                <Route path="/compliance/master-register" element={<MasterRegisterPage />} />
              </Route>
              <Route element={<PermissionRoute permission="privacy.incidents.read" />}>
                <Route path="/privacy/incidents" element={<PrivacyIncidentsPage />} />
                <Route path="/privacy/incidents/:id/view" element={<PrivacyIncidentViewPage />} />
              </Route>
              <Route element={<PermissionRoute permission="privacy.incidents.create" />}><Route path="/privacy/incidents/new" element={<PrivacyIncidentFormPage />} /></Route>
              <Route element={<PermissionRoute permission="privacy.incidents.update" />}><Route path="/privacy/incidents/:id" element={<PrivacyIncidentFormPage />} /></Route>
              <Route element={<PermissionRoute permission="privacy.pias.read" />}>
                <Route path="/privacy/pias" element={<PIAsPage />} />
                <Route path="/privacy/pias/:id/view" element={<PIAViewPage />} />
              </Route>
              <Route element={<PermissionRoute permission="privacy.pias.create" />}><Route path="/privacy/pias/new" element={<PIAFormPage />} /></Route>
              <Route element={<PermissionRoute permission="privacy.pias.update" />}><Route path="/privacy/pias/:id" element={<PIAFormPage />} /></Route>
              <Route element={<PermissionRoute permission="privacy.sars.read" />}>
                <Route path="/privacy/sars" element={<SARsPage />} />
                <Route path="/privacy/sars/:id/view" element={<SARViewPage />} />
              </Route>
              <Route element={<PermissionRoute permission="privacy.sars.create" />}><Route path="/privacy/sars/new" element={<SARFormPage />} /></Route>
              <Route element={<PermissionRoute permission="privacy.sars.update" />}><Route path="/privacy/sars/:id" element={<SARFormPage />} /></Route>
              <Route element={<PermissionRoute permission="management.complaints.read" />}>
                <Route path="/management/complaints" element={<ComplaintsPage />} />
                <Route path="/management/complaints/:id/view" element={<ComplaintViewPage />} />
              </Route>
              <Route element={<PermissionRoute permission="management.complaints.create" />}><Route path="/management/complaints/new" element={<ComplaintFormPage />} /></Route>
              <Route element={<PermissionRoute permission="management.complaints.update" />}><Route path="/management/complaints/:id" element={<ComplaintFormPage />} /></Route>
              <Route element={<PermissionRoute permission="management.distributiongroups.read" />}>
                <Route path="/management/distribution-groups" element={<DistributionGroupsPage />} />
              </Route>
              <Route element={<PermissionRoute anyOf={['admin.users.read', 'admin.roles.read', 'admin.organisations.read', 'admin.auditlogs.read', 'admin.lookups.read', 'admin.notifications.manage', 'admin.settings.read']} />}>
                <Route path="/admin" element={<AdminPage />} />
              </Route>
              <Route element={<PermissionRoute permission="admin.users.read" />}>
                <Route path="/admin/users" element={<UsersPage />} />
                <Route path="/admin/users/:id/view" element={<UserViewPage />} />
              </Route>
              <Route element={<PermissionRoute permission="admin.users.create" />}><Route path="/admin/users/new" element={<UserFormPage />} /></Route>
              <Route element={<PermissionRoute permission="admin.users.update" />}><Route path="/admin/users/:id" element={<UserFormPage />} /></Route>
              <Route element={<PermissionRoute permission="admin.roles.read" />}>
                <Route path="/admin/roles" element={<RolesPage />} />
                <Route path="/admin/roles/:id/view" element={<RoleViewPage />} />
              </Route>
              <Route element={<PermissionRoute permission="admin.roles.create" />}><Route path="/admin/roles/new" element={<RoleFormPage />} /></Route>
              <Route element={<PermissionRoute permission="admin.roles.update" />}><Route path="/admin/roles/:id" element={<RoleFormPage />} /></Route>
              <Route element={<PermissionRoute permission="admin.organisations.read" />}>
                <Route path="/admin/organisations" element={<OrganisationsPage />} />
                <Route path="/admin/organisations/:id/view" element={<OrganisationViewPage />} />
              </Route>
              <Route element={<PermissionRoute permission="admin.organisations.create" />}><Route path="/admin/organisations/new" element={<OrganisationFormPage />} /></Route>
              <Route element={<PermissionRoute permission="admin.organisations.update" />}><Route path="/admin/organisations/:id" element={<OrganisationFormPage />} /></Route>
              <Route element={<PermissionRoute permission="admin.auditlogs.read" />}>
                <Route path="/admin/audit-logs" element={<AuditLogsPage />} />
              </Route>
              <Route element={<PermissionRoute permission="admin.lookups.read" />}>
                <Route path="/admin/lookups" element={<LookupsPage />} />
              </Route>
              <Route element={<PermissionRoute permission="admin.notifications.manage" />}>
                <Route path="/admin/notification-templates" element={<NotificationTemplatesPage />} />
              </Route>
              <Route path="/profile" element={<ProfilePage />} />
              <Route element={<PermissionRoute permission="admin.settings.read" />}>
                <Route path="/settings" element={<SettingsPage />} />
              </Route>
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </Suspense>
          </BrowserRouter>
        </ThemeProvider>
      </ThemeModeContext.Provider>
    </ConfigProvider>
  );
}

export default App;
